import { isCurrentDraftContext, useComposerDraftContext } from "@/features/coach-review/composerDrafts";
import { useMutation } from "@tanstack/react-query";
import {
  createPlayerGoal,
  deletePlayerGoal,
  ackPlayerGoalReflections,
  updatePlayerGoal,
  type ApiError,
} from "@/lib/api";
import { invalidateScope } from "@/lib/queryClient";
import type {
  CreatePlayerGoalRequest,
  PlayerGoal,
  UpdatePlayerGoalRequest,
} from "@/lib/types";

/**
 * Player-goal mutation hooks (M6a).
 *
 * M6a ships the goal create/edit MODAL — the goal CARD render +
 * the per-player goal LIST are M7 (they live inside Player
 * Development; legacy `_renderGoalCard` is only rendered there).
 * So there's no `useCoachGoalsList` query in M6a — only the
 * mutations the modal needs. A `getPlayerGoals` API verb exists
 * for M7 to build the list query on.
 *
 * `useCoachGoalMutations` bundles create / update / delete; each
 * `invalidateScope`s so any goal-reading surface (M7's Player
 * Development, once it lands) refetches.
 */

export type CoachGoalMutations = {
  createGoal: (
    body: CreatePlayerGoalRequest,
    scope?: { teamId: string; seasonId: string | null; seasonMode: "current" | "unseasoned" },
  ) => Promise<{ goal: PlayerGoal }>;
  updateGoal: (
    id: number,
    body: UpdatePlayerGoalRequest,
  ) => Promise<{ goal: PlayerGoal }>;
  deleteGoal: (id: number) => Promise<{ ok: true }>;
  ackReflections: (id: number) => Promise<{ ok: true; cleared: number }>;
  isPending: boolean;
  error: ApiError | null;
};

export function useCoachGoalMutations(): CoachGoalMutations {
  const owner = useComposerDraftContext();
  const create = useMutation({
    meta: { composerContext: owner },
    mutationFn: (input: { body: CreatePlayerGoalRequest; scope?: { teamId: string; seasonId: string | null; seasonMode: "current" | "unseasoned" } }) => {
      if (!owner || !isCurrentDraftContext(owner)) throw new Error("Coaching context changed");
      const scope = input.scope ?? { teamId: owner.teamId, seasonId: owner.seasonId, seasonMode: owner.seasonId === null ? "unseasoned" as const : "current" as const };
      if (scope.teamId !== owner.teamId || scope.seasonId !== owner.seasonId || (owner.seasonId === null && scope.seasonMode !== "unseasoned")) throw new Error("Coaching context changed");
      return createPlayerGoal(input.body, scope);
    },
    onSuccess: () => { if (isCurrentDraftContext(owner)) return invalidateScope(); },
  });
  const update = useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: number;
      body: UpdatePlayerGoalRequest;
    }) => updatePlayerGoal(id, body),
    onSuccess: () => invalidateScope(),
  });
  const remove = useMutation({
    mutationFn: deletePlayerGoal,
    onSuccess: () => invalidateScope(),
  });
  const ack = useMutation({
    mutationFn: ackPlayerGoalReflections,
    onSuccess: () => invalidateScope(),
  });

  const all = [create, update, remove, ack];
  return {
    createGoal: (body, scope) => create.mutateAsync({ body, scope }),
    updateGoal: (id, body) => update.mutateAsync({ id, body }),
    deleteGoal: (id) => remove.mutateAsync(id),
    ackReflections: (id) => ack.mutateAsync(id),
    isPending: all.some((m) => m.isPending),
    error: (all.find((m) => m.error)?.error as ApiError | undefined) ?? null,
  };
}
